import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import Icon from '../../../../components/common/Icon';
import { scrollTop } from '../../../../utils/utilities';
import { ReactComponent as ArrowBackIcon } from '../../../../icons/arrow-left-circle-solid.svg';
import { ReactComponent as ArrowRightTagIcon } from '../../../../icons/arrow-right-tag-solid.svg';
import './ViewNextSongBtn.scss';

const ViewNextSongBtn = ({ onNext, nextSongTitle, nextSongPosition, smooth = true }) => {
  const { t } = useTranslation();
  const [isScrollable, setIsScrollable] = useState(true);
  const scrollTopBtnClass = cx('scroll-top-btn', { standalone: !nextSongTitle });

  // Only show the "back to top" button when the page can actually scroll.
  // Short songs that fit on screen don't need it (and showing it makes the
  // SuperScroll controls flicker in and out).
  useEffect(() => {
    const checkScrollable = () => {
      const doc = document.documentElement;
      setIsScrollable(doc.scrollHeight > doc.clientHeight + 1);
    };

    checkScrollable();

    // Toggling the button changes the body height, which re-triggers the observer within the
    // same delivery cycle and throws "ResizeObserver loop completed with undelivered
    // notifications". Deferring to the next frame breaks that synchronous loop.
    let rafId = null;

    const scheduleCheck = () => {
      if (rafId !== null) return;

      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        checkScrollable();
      });
    };

    const resizeObserver = new ResizeObserver(scheduleCheck);
    resizeObserver.observe(document.body);
    window.addEventListener('resize', checkScrollable);
    window.addEventListener('fontSizeChanged', checkScrollable);

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);

      resizeObserver.disconnect();
      window.removeEventListener('resize', checkScrollable);
      window.removeEventListener('fontSizeChanged', checkScrollable);
    };
  }, []);

  const handleScrollToTop = () => {
    scrollTop(undefined, smooth);
  };

  return (
    <div className="ViewNextSongBtn">
      {nextSongTitle && (
        <div className="next-button text-ellipsis" role="button" onClick={onNext}>
          <Icon icon={<ArrowRightTagIcon />} className="next-btn-icon solid" />
          <span className="next-btn-title">{nextSongTitle}</span>
        </div>
      )}
      {isScrollable && (
        <button
          className={scrollTopBtnClass}
          onClick={handleScrollToTop}
          title={t('common.backToTop')}
          aria-label={t('common.backToTop')}
        >
          <Icon icon={<ArrowBackIcon />} className="back-to-top-icon solid" />
        </button>
      )}
    </div>
  );
};

export default ViewNextSongBtn;
